import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import { Authenticators } from '@customerio/hydra-authentication';
import { session } from '@/services/session';
import { useSignupPublicFlags } from '@/services/signup/public-flags';
import { hasSignupPublicFlag, SignupPublicFeatureFlag } from '@/services/signup/public-flags-store';
import { aliasSignupUser } from '@/services/signup/alias';
import { useRecaptchaBadge } from '@/services/signup/recaptcha';
import {
  buildRegisterAccountBody,
  getPromoCode,
  registerAccount,
  startSignup,
  type PromoCode,
  type SignupPrepopulateData,
} from '@/services/signup/api';
import {
  createDefaultSignupFormValues,
  type NavigateToSignupStep,
  type SignupCompletion,
  type SignupFormValues,
} from './types';
import type { SignupStep } from './utils/steps';
import { getNextStep, resolveSignupType, type ResolvedSignupType } from './utils/flow-sequence';
import { resolveGuardedStep } from './utils/step-guards';
import {
  SignupAnalyticsEvent,
  signupTypeProps,
  trackSignupEvent,
  type SignupAnalyticsEventName,
} from './utils/analytics';
import { captureLandingUrlParams, getUtmPartner } from './utils/landing-params';
import { mapStartupFields } from './utils/startup-fields';
import { resolveSignupErrorMessage } from './utils/signup-error-message';
import { trackSignupProgress } from './utils/track-signup-progress';
import { clearStoredSignupDraft, saveWelcomeDialogAnswers } from './utils/welcome-dialog';

const ALLOWED_SIGNUP_TYPES = new Set(['journeys', 'startup']);

export type OnSignupComplete = (completion: SignupCompletion) => void;

export type SignupFlowValue = {
  // Form state. Steps patch as the visitor answers; nothing is persisted until
  // the final register call.
  formValues: SignupFormValues;
  patchFormValues: (patch: Partial<SignupFormValues>) => void;

  // Onboarding selections.
  messagingUseCases: string[];
  setMessagingUseCases: (value: string[]) => void;
  messageChannels: string[];
  setMessageChannels: (value: string[]) => void;
  onboardingRole: string | null;
  setOnboardingRole: (value: string | null) => void;

  // Flow classification (derived).
  /** Raw `?signupType=` — unvalidated; prefer `effectiveSignupType`. */
  signupType: string | undefined;
  /**
   * The validated signup type this funnel run operates as: unknown values
   * coerce to `journeys`, and `startup` requires the `signup_startup_form`
   * public flag. Step analytics pass this as `Signup Type`.
   */
  effectiveSignupType: string;
  isStartup: boolean;
  isGoogleSignup: boolean;
  isSignupDarkModeEnabled: boolean;
  /** `signup_skip_onboarding_questions` is on: the data-center step replaces the product questions. */
  skipsOnboardingQuestions: boolean;
  resolvedSignupType: ResolvedSignupType;

  // Promo.
  promoCode: string | undefined;
  /** Set once the URL promo validates; stays `null` for unknown/redeemed codes. */
  validatedPromo: PromoCode | null;

  /**
   * The landing `utm_partner`, from the same capture that feeds the application's
   * attribution field. Drives the startup form's incubator/VC prefill.
   */
  utmPartner: string | null;

  // Sequence.
  currentStep: SignupStep;
  /**
   * Advance the funnel: fires the step's server-side identify, then navigates
   * to the next step — or, at the end of the sequence, creates the account
   * (register → session flip → welcome-dialog handoff → analytics alias).
   */
  goToNextStep: (options?: { skipTracking?: boolean }) => Promise<void>;
  /** The end-of-sequence register is in flight. */
  isCompletingSignup: boolean;

  /**
   * The entry step's submit plumbing: reCAPTCHA-guarded `account_signup/start`,
   * prepopulate handling, the `NameAndEmailSubmitted` event, and the first
   * `goToNextStep`. Errors propagate — the entry step owns their display.
   */
  beginSignup: (email: string) => Promise<SignupPrepopulateData>;

  trackSignupEvent: (event: SignupAnalyticsEventName, properties?: Record<string, unknown>) => void;
};

const SignupFlowContext = createContext<SignupFlowValue | null>(null);

export function useSignupFlow(): SignupFlowValue {
  const value = use(SignupFlowContext);
  if (value == null) {
    throw new Error('useSignupFlow must be used within a SignupFlowProvider');
  }
  return value;
}

/** A ref that always holds the latest render's value, for same-tick engine reads. */
function useLiveRef<T>(value: T): RefObject<T> {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

/**
 * `useState` with a write-through ref. Step submits set state and call the
 * flow engine in the same tick, so the engine reads through refs — a plain
 * closure would still see the previous render's values.
 */
function useStateWithRef<T>(initial: T): [T, (next: T) => void, RefObject<T>] {
  const ref = useRef(initial);
  const [value, setValue] = useState(initial);
  const set = useCallback((next: T) => {
    ref.current = next;
    setValue(next);
  }, []);
  return [value, set, ref];
}

/** Props/flags the engine reads through a ref (they never change mid-tick from a step action). */
type FlowFlags = {
  isStartup: boolean;
  skipsOnboardingQuestions: boolean;
  effectiveSignupType: string;
  promoCode: string | undefined;
};

/**
 * Classify the funnel run from its URL params + public flags: unknown signup
 * types coerce to `journeys`, and a startup arrival without the
 * `signup_startup_form` flag downgrades to `journeys`.
 */
function classifySignupFlow(
  signupType: string | undefined,
  promoCode: string | undefined,
  flags: Parameters<typeof hasSignupPublicFlag>[0],
): FlowFlags & { isSignupDarkModeEnabled: boolean } {
  const validatedSignupType = signupType != null && ALLOWED_SIGNUP_TYPES.has(signupType) ? signupType : 'journeys';
  const isStartup =
    validatedSignupType === 'startup' && hasSignupPublicFlag(flags, SignupPublicFeatureFlag.SignupStartupForm);
  return {
    isStartup,
    skipsOnboardingQuestions: hasSignupPublicFlag(flags, SignupPublicFeatureFlag.SignupSkipOnboardingQuestions),
    effectiveSignupType: validatedSignupType === 'startup' && !isStartup ? 'journeys' : validatedSignupType,
    promoCode,
    isSignupDarkModeEnabled: hasSignupPublicFlag(flags, SignupPublicFeatureFlag.SignupDarkMode),
  };
}

/** The sequence context the funnel-integrity guard keys on. */
function guardContext(
  formValues: SignupFormValues,
  { isStartup, skipsOnboardingQuestions }: Pick<FlowFlags, 'isStartup' | 'skipsOnboardingQuestions'>,
): Parameters<typeof resolveGuardedStep>[1] {
  return {
    hasEmail: formValues.email.trim() !== '',
    isStartup,
    isGoogleSignup: formValues.isGoogleSignup === true,
    skipsOnboardingQuestions,
  };
}

/**
 * Apply the funnel-integrity guards: flow state lives in memory only, so deep
 * links and refreshes past the entry step have nothing to work with — the
 * provider renders nothing and bounces to the guard's target step.
 */
function useStepGuard(
  step: SignupStep,
  context: Parameters<typeof resolveGuardedStep>[1],
  onNavigateStep: NavigateToSignupStep,
): boolean {
  const guardedStep = resolveGuardedStep(step, context);
  const isGuarded = guardedStep !== step;
  useEffect(() => {
    if (isGuarded) {
      // Replace, never push: the bounce must consume the rejected URL, else
      // Back returns to it and the guard bounces again — trapping the visitor.
      onNavigateStep(guardedStep, { replace: true });
    }
  }, [isGuarded, guardedStep, onNavigateStep]);
  return isGuarded;
}

/**
 * The join-request hand-off: that screen already collected and confirmed the
 * verification code, so the funnel rebuilds the minimal state from the email
 * and lands on the password step — where confirming the code would have landed.
 * Deliberately does *not* call `beginSignup`: `account_signup/start` would send
 * a second code. Account creation re-checks the confirmed verification, so
 * trusting the email to skip the code step is safe.
 *
 * Returns whether the hand-off is still pending, so the caller renders nothing
 * until it lands — the entry form must never flash, and its viewed event must
 * not fire.
 */
function useResumeHandoff(
  resumeEmail: string | undefined,
  patchFormValues: (patch: Partial<SignupFormValues>) => void,
  onNavigateStep: NavigateToSignupStep,
): boolean {
  // Trimmed to the guard's own invariant: whitespace would resume, bounce, resume.
  const email = resumeEmail?.trim() ?? '';
  const isResuming = email !== '';

  useEffect(() => {
    if (!isResuming) {
      return;
    }
    patchFormValues({ email, isGoogleSignup: false });
    // Replace: the resume URL is a hand-off, not a place Back should return to.
    onNavigateStep('password', { replace: true });
  }, [isResuming, email, patchFormValues, onNavigateStep]);

  return isResuming;
}

/** The funnel's writable state: form values plus the onboarding selections. */
function useSignupFlowState(initialFormValues: Partial<SignupFormValues> | undefined) {
  const [formValues, setFormValuesInternal, formValuesRef] = useStateWithRef<SignupFormValues>(
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial state only; the copy below handles a prefill that arrives later
    useMemo(() => ({ ...createDefaultSignupFormValues(), ...initialFormValues }), []),
  );

  // Copied once, during render: a refresh past the entry step mounts before the
  // route supplies the prefill, and the form reads it as its default on mount.
  const [hasCopiedPrefill, setHasCopiedPrefill] = useState(initialFormValues != null);
  if (!hasCopiedPrefill && initialFormValues != null) {
    setHasCopiedPrefill(true);
    setFormValuesInternal({ ...formValuesRef.current, ...initialFormValues });
  }

  const [messagingUseCases, setMessagingUseCases, messagingUseCasesRef] = useStateWithRef<string[]>([]);
  const [messageChannels, setMessageChannels, messageChannelsRef] = useStateWithRef<string[]>([]);
  const [onboardingRole, setOnboardingRole, onboardingRoleRef] = useStateWithRef<string | null>(null);

  const patchFormValues = useCallback(
    (patch: Partial<SignupFormValues>) => {
      setFormValuesInternal({ ...formValuesRef.current, ...patch });
    },
    [setFormValuesInternal, formValuesRef],
  );

  return {
    formValues,
    formValuesRef,
    patchFormValues,
    messagingUseCases,
    setMessagingUseCases,
    messagingUseCasesRef,
    messageChannels,
    setMessageChannels,
    messageChannelsRef,
    onboardingRole,
    setOnboardingRole,
    onboardingRoleRef,
  };
}

type SignupFlowState = ReturnType<typeof useSignupFlowState>;

/**
 * One-time funnel-entry side effects and captures: the landing-param capture
 * (before step navigation rewrites the URL), the `utm_partner` it recorded, and
 * URL-promo validation for the coupon banner (fire-and-forget; signup is never
 * gated on it).
 */
function useEntryEffects(promoCode: string | undefined) {
  const [validatedPromo, setValidatedPromo] = useState<PromoCode | null>(null);

  // The landing capture runs here rather than in an effect because the startup
  // form needs `utm_partner` for its first render, one commit earlier. Both
  // values come from the module, so its sticky-re-entry rule governs the
  // partner too — reading the URL directly would ignore it. Safe to run during
  // render: the capture is a pure function of this URL, so a StrictMode double
  // invocation writes the same values.
  const [utmPartner] = useState(() => {
    captureLandingUrlParams(Object.fromEntries(new URLSearchParams(window.location.search)));
    return getUtmPartner();
  });

  useEffect(() => {
    clearStoredSignupDraft();
  }, []);

  useEffect(() => {
    if (promoCode == null || promoCode === '') {
      return;
    }
    let cancelled = false;
    void getPromoCode(promoCode).then((promo) => {
      if (!cancelled) {
        setValidatedPromo(promo);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [promoCode]);

  return { validatedPromo, utmPartner };
}

/**
 * End of sequence: create the account. Order: register → hand the completion
 * payload to the page (the session flip unmounts this provider, so anything
 * `Signup: Completed` needs is captured first) → flip the local session (the
 * server already set the cookie; `AccountCreation` is the no-op authenticator
 * for exactly this) → persist the welcome-dialog answers for the authenticated
 * app → alias the anonymous analytics id to the new user. The auth flip lands
 * on the page's `isAuthenticated` branch, which waits out account readiness
 * before redirecting into the app.
 */
function useCompleteSignup(
  state: SignupFlowState,
  flowFlagsRef: RefObject<FlowFlags>,
  onSignupCompleteRef: RefObject<OnSignupComplete | undefined>,
) {
  const snackbars = useSnackbars();
  const [isCompletingSignup, setIsCompletingSignup] = useState(false);
  // Synchronous re-entrancy guard: `isCompletingSignup` state flushes a render
  // late, so a rapid double submit at the last step could otherwise fire two
  // register calls before the first one resolves.
  const isCompletingRef = useRef(false);
  const { formValuesRef, patchFormValues, messagingUseCasesRef, messageChannelsRef, onboardingRoleRef } = state;

  const completeSignup = useCallback(async () => {
    if (isCompletingRef.current) {
      return;
    }
    isCompletingRef.current = true;

    const { isStartup, promoCode } = flowFlagsRef.current;
    const signupType = resolveSignupType({ isStartup });
    patchFormValues({ signupType });

    // The pre-creation application POST persists nothing — `meta.startup` on
    // the register call is what lands in `account_meta`, where Flightdeck and
    // the review pipeline read a startup signup's context from.
    const startupFields = isStartup ? mapStartupFields(formValuesRef.current) : undefined;

    // The raw trimmed URL code goes to the register call — the server is the
    // promo authority; `validatedPromo` only drives the banner. An invalid
    // code fails the register with promo-specific copy (below) rather than
    // being silently dropped, which could lose a legitimate promo whose
    // pre-validation merely failed transiently.
    const promo = promoCode?.trim();
    const registerPromoCode = promo != null && promo !== '' ? promo : undefined;

    setIsCompletingSignup(true);
    try {
      const body = buildRegisterAccountBody({
        values: formValuesRef.current,
        messagingUseCases: messagingUseCasesRef.current,
        optInChannels: [],
        emailVolume: null,
        role: onboardingRoleRef.current,
        messageChannels: messageChannelsRef.current,
        promoCode: registerPromoCode,
        startupFields,
      });
      const { account, user } = await registerAccount(body);
      onSignupCompleteRef.current?.({
        accountId: account.id,
        signupType,
        isGoogleSignup: formValuesRef.current.isGoogleSignup,
        isStartup,
      });
      await session.authenticate(Authenticators.AccountCreation, undefined);
      saveWelcomeDialogAnswers({
        messagingUseCases: messagingUseCasesRef.current,
        v2MessageChannels: messageChannelsRef.current,
        v2Role: onboardingRoleRef.current,
      });
      await aliasSignupUser(user.id);
    } catch (error) {
      snackbars.create({
        variant: 'critical',
        id: 'signup.error',
        message: resolveSignupErrorMessage(error, registerPromoCode),
      });
    } finally {
      isCompletingRef.current = false;
      setIsCompletingSignup(false);
    }
  }, [
    flowFlagsRef,
    formValuesRef,
    messagingUseCasesRef,
    messageChannelsRef,
    onboardingRoleRef,
    onSignupCompleteRef,
    patchFormValues,
    snackbars,
  ]);

  return { completeSignup, isCompletingSignup };
}

/**
 * The sequencing engine: `goToNextStep` + the entry step's `beginSignup`
 * plumbing. Step submits patch state and advance in the same tick, so the
 * engine reads its inputs through live refs rather than render closures.
 */
function useSignupEngine(
  state: SignupFlowState,
  flowFlags: FlowFlags,
  step: SignupStep,
  onNavigateStep: NavigateToSignupStep,
  onSignupComplete: OnSignupComplete | undefined,
) {
  const { formValuesRef, patchFormValues } = state;
  const flowFlagsRef = useLiveRef(flowFlags);
  const stepRef = useLiveRef(step);
  const onSignupCompleteRef = useLiveRef(onSignupComplete);
  const { completeSignup, isCompletingSignup } = useCompleteSignup(state, flowFlagsRef, onSignupCompleteRef);

  const goToNextStep = useCallback(
    async ({ skipTracking = false }: { skipTracking?: boolean } = {}) => {
      if (!skipTracking) {
        trackSignupProgress(stepRef.current, formValuesRef.current, flowFlagsRef.current);
      }

      const { isStartup, skipsOnboardingQuestions } = flowFlagsRef.current;
      const next = getNextStep(stepRef.current, {
        isStartup,
        isGoogleSignup: formValuesRef.current.isGoogleSignup === true,
        skipsOnboardingQuestions,
      });

      // Off-sequence steps are the guards' job; advancing from one is a no-op.
      if (next == null) {
        return;
      }

      if (next === 'complete') {
        await completeSignup();
        return;
      }

      onNavigateStep(next);
    },
    [completeSignup, onNavigateStep, stepRef, flowFlagsRef, formValuesRef],
  );

  const runBeginSignup = useCallback(
    async (email: string): Promise<SignupPrepopulateData> => {
      patchFormValues({ email });
      const { effectiveSignupType } = flowFlagsRef.current;

      const prepopulate = await startSignup({
        email,
        isGoogleSignup: formValuesRef.current.isGoogleSignup,
        signupType: effectiveSignupType,
      });

      const values = formValuesRef.current;
      patchFormValues({
        ...(values.companyName === '' ? { companyName: prepopulate.companyName } : {}),
        ...(values.role === '' ? { role: prepopulate.role } : {}),
      });

      trackSignupEvent(
        SignupAnalyticsEvent.NameAndEmailSubmitted,
        signupTypeProps({ signupType: effectiveSignupType, isGoogleSignup: formValuesRef.current.isGoogleSignup }),
      );

      await goToNextStep();
      return prepopulate;
    },
    [patchFormValues, goToNextStep, flowFlagsRef, formValuesRef],
  );

  // Single-flight: `beginSignup` has a long async window (the start call), and
  // each completion advances from the *current* step — a second submit landing
  // after the first advanced off the entry step would advance again and skip
  // the confirmation (email-verification) step. Concurrent submits share the
  // in-flight run instead.
  const inflightBeginRef = useRef<Promise<SignupPrepopulateData> | null>(null);
  const beginSignup = useCallback(
    (email: string): Promise<SignupPrepopulateData> => {
      inflightBeginRef.current ??= runBeginSignup(email).finally(() => {
        inflightBeginRef.current = null;
      });
      return inflightBeginRef.current;
    },
    [runBeginSignup],
  );

  return { goToNextStep, beginSignup, isCompletingSignup };
}

type SignupFlowProviderProps = {
  children: ReactNode;
  step: SignupStep;
  onNavigateStep: NavigateToSignupStep;
  signupType: string | undefined;
  promoCode: string | undefined;
  /** See `SignupPageProps.initialFormValues`. */
  initialFormValues?: Partial<SignupFormValues>;
  /** See `SignupPageProps.resumeEmail`. */
  resumeEmail?: string;
  /**
   * Called when the register succeeds, before the session flip — the flip
   * unmounts this provider, so the page captures here what the post-auth
   * `Signup: Completed` event needs.
   */
  onSignupComplete?: OnSignupComplete;
};

/**
 * The signup funnel's state + sequencing engine. Steps consume it through
 * `useSignupFlow`: patch answers, fire their step event, call an API-module
 * function if they have one, then `goToNextStep`.
 *
 * The engine deliberately owns everything that is invisible in the UI and so
 * easiest to drop: the per-step server-side identify, the final register
 * payload (attribution cookies included), the session flip, the
 * welcome-dialog handoff, and the anonymous-id alias.
 */
export function SignupFlowProvider({
  children,
  step,
  onNavigateStep,
  signupType,
  promoCode,
  initialFormValues,
  resumeEmail,
  onSignupComplete,
}: SignupFlowProviderProps) {
  const flags = useSignupPublicFlags();
  useRecaptchaBadge();

  const classification = classifySignupFlow(signupType, promoCode, flags);
  const { isStartup, skipsOnboardingQuestions, effectiveSignupType, isSignupDarkModeEnabled } = classification;

  const state = useSignupFlowState(initialFormValues);

  const isResuming = useResumeHandoff(resumeEmail, state.patchFormValues, onNavigateStep);
  const { validatedPromo, utmPartner } = useEntryEffects(promoCode);
  const { goToNextStep, beginSignup, isCompletingSignup } = useSignupEngine(
    state,
    classification,
    step,
    onNavigateStep,
    onSignupComplete,
  );

  // The guard needs the full sequence context: a step renders only while the
  // active sequence contains it, so history back/forward can't resurface a
  // step the visitor's answers have since removed.
  const isGuarded = useStepGuard(step, guardContext(state.formValues, classification), onNavigateStep);
  const value = useSignupFlowValue({
    state,
    signupType,
    effectiveSignupType,
    isStartup,
    skipsOnboardingQuestions,
    isSignupDarkModeEnabled,
    promoCode,
    validatedPromo,
    utmPartner,
    step,
    goToNextStep,
    isCompletingSignup,
    beginSignup,
  });

  if (isGuarded || isResuming) {
    return null;
  }

  return <SignupFlowContext value={value}>{children}</SignupFlowContext>;
}

type SignupFlowValueArgs = {
  state: SignupFlowState;
  signupType: string | undefined;
  effectiveSignupType: string;
  isStartup: boolean;
  skipsOnboardingQuestions: boolean;
  isSignupDarkModeEnabled: boolean;
  promoCode: string | undefined;
  validatedPromo: PromoCode | null;
  utmPartner: string | null;
  step: SignupStep;
  goToNextStep: SignupFlowValue['goToNextStep'];
  isCompletingSignup: boolean;
  beginSignup: SignupFlowValue['beginSignup'];
};

/** Assemble the context value; a memo so consumers re-render only on real change. */
function useSignupFlowValue(args: SignupFlowValueArgs): SignupFlowValue {
  const {
    state,
    signupType,
    effectiveSignupType,
    isStartup,
    skipsOnboardingQuestions,
    isSignupDarkModeEnabled,
    promoCode,
    validatedPromo,
    utmPartner,
    step,
    goToNextStep,
    isCompletingSignup,
    beginSignup,
  } = args;

  return useMemo<SignupFlowValue>(
    () => ({
      formValues: state.formValues,
      patchFormValues: state.patchFormValues,
      messagingUseCases: state.messagingUseCases,
      setMessagingUseCases: state.setMessagingUseCases,
      messageChannels: state.messageChannels,
      setMessageChannels: state.setMessageChannels,
      onboardingRole: state.onboardingRole,
      setOnboardingRole: state.setOnboardingRole,
      signupType,
      effectiveSignupType,
      isStartup,
      isGoogleSignup: state.formValues.isGoogleSignup === true,
      isSignupDarkModeEnabled,
      skipsOnboardingQuestions,
      resolvedSignupType: resolveSignupType({ isStartup }),
      promoCode,
      validatedPromo,
      utmPartner,
      currentStep: step,
      goToNextStep,
      isCompletingSignup,
      beginSignup,
      trackSignupEvent,
    }),
    [
      state.formValues,
      state.patchFormValues,
      state.messagingUseCases,
      state.setMessagingUseCases,
      state.messageChannels,
      state.setMessageChannels,
      state.onboardingRole,
      state.setOnboardingRole,
      signupType,
      effectiveSignupType,
      isStartup,
      skipsOnboardingQuestions,
      isSignupDarkModeEnabled,
      promoCode,
      validatedPromo,
      utmPartner,
      step,
      goToNextStep,
      isCompletingSignup,
      beginSignup,
    ],
  );
}
